import { useEffect, useId, useMemo, useState } from 'react'
import ApiService from '@services/api'
import ChartComponent from '@components/dashboard/ChartComponent'
import { useNotification } from '@context/NotificationContext'
import { CHART_THEME, DATA_SERIES_COLORS, STATUS_SERIES_COLORS, VISUAL_COLORS } from '@config/visualTheme'
import './processReportsEnhancements.css'

const EMPTY_FILTERS = {
  clienteId: '',
  area: '',
  entradaInicio: '', entradaFim: '', distribuicaoInicio: '', distribuicaoFim: '', encerramentoInicio: '', encerramentoFim: '',
}

function normalizeSearch(value) {
  return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR')
}

function buildQuery(filters = {}, extras = {}) {
  const params = new URLSearchParams()
  Object.entries({ ...filters, ...extras }).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
  })
  return params.toString()
}

function ClientAutocomplete({ clients, disabled, value, onChange }) {
  const listboxId = useId()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  useEffect(() => {
    const selectedClient = clients.find((client) => String(client.id) === String(value))
    if (selectedClient) setQuery(selectedClient.nome)
    else if (!value) setQuery('')
  }, [clients, value])

  const suggestions = useMemo(() => {
    const normalizedQuery = normalizeSearch(query.trim())
    const matchingClients = normalizedQuery
      ? clients.filter((client) => normalizeSearch(client.nome).includes(normalizedQuery))
      : clients
    return matchingClients.slice(0, 8)
  }, [clients, query])

  const selectClient = (client) => {
    setQuery(client.nome)
    onChange(String(client.id))
    setOpen(false)
    setActiveIndex(-1)
  }

  const handleInputChange = (event) => {
    setQuery(event.target.value)
    onChange('')
    setOpen(true)
    setActiveIndex(-1)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false)
      setActiveIndex(-1)
      return
    }

    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && suggestions.length) {
      event.preventDefault()
      setOpen(true)
      const direction = event.key === 'ArrowDown' ? 1 : -1
      setActiveIndex((current) => {
        const next = current + direction
        if (next < 0) return suggestions.length - 1
        if (next >= suggestions.length) return 0
        return next
      })
      return
    }

    if (event.key === 'Enter' && open && activeIndex >= 0 && suggestions[activeIndex]) {
      event.preventDefault()
      selectClient(suggestions[activeIndex])
    }
  }

  return (
    <label className="report-client-filter">
      <span>Cliente *</span>
      <div className="report-client-combobox">
        <i className="fas fa-magnifying-glass" aria-hidden="true" />
        <input
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={open}
          aria-activedescendant={activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
          autoComplete="off"
          value={query}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          onBlur={() => { setOpen(false); setActiveIndex(-1) }}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? 'Carregando clientes...' : 'Digite para buscar um cliente'}
          disabled={disabled}
          required
        />
        {open && !disabled ? (
          <ul id={listboxId} className="report-client-options" role="listbox">
            {suggestions.length ? suggestions.map((client, index) => (
              <li
                id={`${listboxId}-option-${index}`}
                key={client.id}
                role="option"
                aria-selected={index === activeIndex}
                className={index === activeIndex ? 'active' : ''}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => selectClient(client)}
              >
                <i className="far fa-building" aria-hidden="true" />
                <span>{client.nome}</span>
              </li>
            )) : <li className="report-client-no-results">Nenhum cliente encontrado.</li>}
          </ul>
        ) : null}
      </div>
      <small>Busque pelo nome e selecione uma opção da lista.</small>
    </label>
  )
}

function formatNumber(value) {
  return new Intl.NumberFormat('pt-BR').format(value || 0)
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)
}

function formatDateTime(value) {
  return value ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value)) : '—'
}

function formatDate(value) {
  if (!value) return ''
  const [year, month, day] = value.split('-')
  return `${day}/${month}/${year}`
}

function describePeriods(filters) {
  const periods = [
    ['Entrada', filters.entradaInicio, filters.entradaFim],
    ['Distribuição', filters.distribuicaoInicio, filters.distribuicaoFim],
    ['Encerramento', filters.encerramentoInicio, filters.encerramentoFim],
  ].filter(([, start, end]) => start || end)

  const area = filters.area ? [`Área: ${filters.area}`] : []
  if (!periods.length) return [...area, 'Todos os períodos'].join(' · ')
  return [...area, ...periods.map(([label, start, end]) => {
    if (start && end) return `${label}: ${formatDate(start)} a ${formatDate(end)}`
    return `${label}: ${start ? `desde ${formatDate(start)}` : `até ${formatDate(end)}`}`
  })].join(' · ')
}

function Stat({ icon, label, value, detail, tone = '' }) {
  return (
    <article className={`report-stat ${tone}`}>
      <div><span>{label}</span><strong>{value}</strong>{detail ? <small>{detail}</small> : null}</div>
      <i className={`fas ${icon}`} />
    </article>
  )
}

function Ranking({ title, items }) {
  return (
    <section className="report-card report-ranking">
      <div className="report-card-title"><h3>{title}</h3><span>Top {Math.min(items?.length || 0, 12)}</span></div>
      {items?.length ? items.map((item) => (
        <div className="report-ranking-row" key={item.nome}>
          <div><span>{item.nome}</span><small>{item.percentual}% do total</small></div>
          <strong>{formatNumber(item.total)}</strong>
        </div>
      )) : <div className="report-empty compact">Sem dados para o recorte selecionado.</div>}
    </section>
  )
}

function AreaSummary({ items }) {
  return (
    <section className="report-card report-area-summary">
      <div className="report-card-title">
        <div><span>Separação por área do escritório</span><h3>Resumo operacional e financeiro</h3></div>
        <span>{formatNumber(items?.length)} áreas</span>
      </div>
      <div className="report-table-scroll">
        <table>
          <thead>
            <tr>
              <th>Área</th><th>Total</th><th>Em andamento</th><th>Encerrados</th><th>Arquivados</th>
              <th>Possível</th><th>Remota</th><th>Provável</th>
            </tr>
          </thead>
          <tbody>
            {items?.length ? items.map((item) => (
              <tr key={item.area}>
                <td><strong>{item.area}</strong></td>
                <td>{formatNumber(item.total)}</td>
                <td>{formatNumber(item.emAndamento)}</td>
                <td>{formatNumber(item.encerrados)}</td>
                <td>{formatNumber(item.arquivados)}</td>
                <td>{formatCurrency(item.valorProvisaoPossivel)}</td>
                <td>{formatCurrency(item.valorProvisaoRemota)}</td>
                <td>{formatCurrency(item.valorProvisaoProvavel)}</td>
              </tr>
            )) : <tr><td colSpan={8}>Sem dados por área para o recorte selecionado.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function ProcessList({ page, loading, search, onSearchChange, onSearch, onPageChange }) {
  return (
    <section className="report-card report-process-list">
      <div className="report-card-title report-process-list-header">
        <div><span>Base processual</span><h3>Processos e última publicação</h3></div>
        <form onSubmit={onSearch} className="report-process-search">
          <label htmlFor="report-process-search">Buscar processo</label>
          <div><i className="fas fa-magnifying-glass" /><input id="report-process-search" value={search} onChange={(event) => onSearchChange(event.target.value)} placeholder="Número, parte ou objeto" /><button type="submit" className="btn-secondary">Buscar</button></div>
        </form>
      </div>
      {loading ? <div className="report-empty compact"><i className="fas fa-spinner fa-spin" /> Carregando processos...</div> : (
        <>
          <div className="report-table-scroll">
            <table>
              <thead>
                <tr><th>Processo</th><th>Área e objeto</th><th>Status</th><th>Perspectiva</th><th>Provisões</th><th>Última publicação</th></tr>
              </thead>
              <tbody>
                {page?.itens?.length ? page.itens.map((item) => (
                  <tr key={item.processoId}>
                    <td>
                      <strong>{item.numeroProcesso || `#${item.processoId}`}</strong>
                      <small>{item.partePrincipal || 'Parte não informada'} × {item.parteContraria || 'Parte não informada'}</small>
                      <small>Profissão/cargo do reclamante: {item.cargoEmpregado || 'Não informado'}</small>
                    </td>
                    <td><strong>{item.area || 'Não informada'}</strong><small title={item.objeto || ''}>{item.objeto || 'Objeto não informado'}</small></td>
                    <td><span className="report-status-pill">{item.status || 'Não informado'}</span><small>{item.situacao || 'Situação não informada'}</small></td>
                    <td><strong>{item.perspectivaPerda || 'Não informada'}</strong></td>
                    <td className="report-provision-cell">
                      <small>Valor da causa: <strong>{item.valorInicialCausa == null ? 'Não informado' : formatCurrency(item.valorInicialCausa)}</strong></small>
                      <small>Possível: <strong>{formatCurrency(item.valorProvisaoPossivel)}</strong></small>
                      <small>Remota: <strong>{formatCurrency(item.valorProvisaoRemota)}</strong></small>
                      <small>Provável: <strong>{formatCurrency(item.valorProvisaoProvavel)}</strong></small>
                    </td>
                    <td><strong>{item.dataUltimaPublicacao ? formatDate(item.dataUltimaPublicacao) : 'Sem publicação'}</strong></td>
                  </tr>
                )) : <tr><td colSpan={6}>Nenhum processo encontrado para o recorte.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="report-pagination">
            <span>{formatNumber(page?.total)} processos · página {page?.pagina || 0} de {page?.totalPaginas || 0}</span>
            <div>
              <button type="button" className="btn-secondary" disabled={!page || page.pagina <= 1} onClick={() => onPageChange(page.pagina - 1)}><i className="fas fa-chevron-left" /> Anterior</button>
              <button type="button" className="btn-secondary" disabled={!page || page.pagina >= page.totalPaginas} onClick={() => onPageChange(page.pagina + 1)}>Próxima <i className="fas fa-chevron-right" /></button>
            </div>
          </div>
        </>
      )}
    </section>
  )
}

function GeneralProcessReport() {
  const notify = useNotification()
  const [clientes, setClientes] = useState([])
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState(null)
  const [dashboard, setDashboard] = useState(null)
  const [processPage, setProcessPage] = useState(null)
  const [processSearch, setProcessSearch] = useState('')
  const [loadingClients, setLoadingClients] = useState(true)
  const [loading, setLoading] = useState(false)
  const [loadingProcesses, setLoadingProcesses] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [includeInternalNotes, setIncludeInternalNotes] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    let retryId

    const loadClients = async () => {
      const result = await ApiService.getProcessReportClients()
      if (!active) return

      if (result.success) {
        setClientes(Array.isArray(result.data) ? result.data : [])
        setError('')
        setLoadingClients(false)
        return
      }

      setError('A base analítica ainda está sendo preparada. Tentaremos novamente em instantes.')
      retryId = window.setTimeout(loadClients, 5000)
    }

    loadClients()
    return () => {
      active = false
      window.clearTimeout(retryId)
    }
  }, [])

  const loadProcessPage = async (selectedFilters, page = 1, search = processSearch) => {
    if (!selectedFilters?.clienteId) return
    setLoadingProcesses(true)
    const query = buildQuery(selectedFilters, { pagina: page, tamanhoPagina: 25, busca: search.trim() })
    const result = await ApiService.request(`relatorios/processos/lista?${query}`)
    if (result.success) setProcessPage(result.data)
    else notify.showError(result.error || 'Não foi possível carregar a listagem de processos.')
    setLoadingProcesses(false)
  }

  useEffect(() => {
    if (!appliedFilters?.clienteId) return
    let active = true
    setLoading(true)
    setLoadingProcesses(true)
    setProcessPage(null)
    setError('')
    setProcessSearch('')

    Promise.all([
      ApiService.getProcessReportDashboard(appliedFilters),
      ApiService.request(`relatorios/processos/lista?${buildQuery(appliedFilters, { pagina: 1, tamanhoPagina: 25 })}`),
    ]).then(([dashboardResult, processResult]) => {
      if (!active) return
      if (dashboardResult.success) setDashboard(dashboardResult.data)
      else setError(dashboardResult.error || 'Não foi possível carregar o relatório.')
      if (processResult.success) setProcessPage(processResult.data)
      else setError(processResult.error || 'Não foi possível carregar a listagem de processos.')
      setLoadingProcesses(false)
      setLoading(false)
    })

    return () => { active = false }
  }, [appliedFilters])

  const trendData = useMemo(() => ({
    labels: (dashboard?.tendenciaMensal || []).map((item) => {
      const [year, month] = item.competencia.split('-')
      return `${month}/${year}`
    }),
    datasets: [
      { label: 'Entradas', data: (dashboard?.tendenciaMensal || []).map((item) => item.entradas), borderColor: CHART_THEME.linePrimary, backgroundColor: CHART_THEME.linePrimaryFill, tension: .35, fill: true },
      { label: 'Encerramentos', data: (dashboard?.tendenciaMensal || []).map((item) => item.encerramentos), borderColor: CHART_THEME.lineSecondary, backgroundColor: CHART_THEME.lineSecondaryFill, tension: .35 },
    ],
  }), [dashboard])

  const stateData = useMemo(() => ({
    labels: (dashboard?.porEstado || []).slice(0, 8).map((item) => item.nome),
    datasets: [{ label: 'Processos', data: (dashboard?.porEstado || []).slice(0, 8).map((item) => item.total), backgroundColor: [...DATA_SERIES_COLORS] }],
  }), [dashboard])

  const areaData = useMemo(() => ({
    labels: (dashboard?.porArea || []).map((item) => item.nome),
    datasets: [{ label: 'Processos', data: (dashboard?.porArea || []).map((item) => item.total), backgroundColor: VISUAL_COLORS.emerald, borderRadius: 1, borderSkipped: false }],
  }), [dashboard])

  const objectData = useMemo(() => ({
    labels: (dashboard?.porObjeto || []).map((item) => item.nome),
    datasets: [{ label: 'Processos', data: (dashboard?.porObjeto || []).map((item) => item.total), backgroundColor: VISUAL_COLORS.teal, borderRadius: 1, borderSkipped: false }],
  }), [dashboard])

  const lossData = useMemo(() => ({
    labels: (dashboard?.porPerspectivaPerda || []).map((item) => item.nome),
    datasets: [{ label: 'Processos', data: (dashboard?.porPerspectivaPerda || []).map((item) => item.total), backgroundColor: [...STATUS_SERIES_COLORS, VISUAL_COLORS.muted, VISUAL_COLORS.violet] }],
  }), [dashboard])

  const provisionData = useMemo(() => ({
    labels: ['Possível', 'Remota', 'Provável'],
    datasets: [{ label: 'Valor provisionado', data: [dashboard?.valorProvisaoPossivel || 0, dashboard?.valorProvisaoRemota || 0, dashboard?.valorProvisaoProvavel || 0], backgroundColor: [VISUAL_COLORS.amber, VISUAL_COLORS.muted, VISUAL_COLORS.red], borderRadius: 1, borderSkipped: false }],
  }), [dashboard])

  const horizontalChartOptions = useMemo(() => ({
    indexAxis: 'y',
    plugins: { legend: { display: false } },
    scales: { x: { beginAtZero: true, ticks: { precision: 0 } }, y: { grid: { display: false } } },
  }), [])

  const currencyChartOptions = useMemo(() => ({
    plugins: { legend: { display: false } },
    scales: { y: { beginAtZero: true, ticks: { callback: (value) => formatCurrency(value) } } },
  }), [])

  const updateFilter = (name, value) => setFilters((current) => ({
    ...current,
    [name]: value,
    ...(name === 'clienteId' ? { area: '' } : {}),
  }))

  const applyFilters = (event) => {
    event.preventDefault()
    if (!filters.clienteId) return
    setAppliedFilters({ ...filters })
  }

  const clearFilters = () => setFilters({ ...EMPTY_FILTERS, clienteId: filters.clienteId })

  const exportExcel = async () => {
    if (!appliedFilters?.clienteId || exporting) return

    setExporting(true)
    const result = await ApiService.exportProcessReportExcel(appliedFilters, includeInternalNotes)
    setExporting(false)

    if (!result.success || !result.data) {
      notify.showError(result.error || 'Não foi possível exportar o relatório para Excel.')
      return
    }

    const selectedClient = clientes.find((cliente) => String(cliente.id) === String(appliedFilters.clienteId))
    const safeClientName = String(selectedClient?.nome || 'cliente')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase()
    const blobUrl = window.URL.createObjectURL(result.data)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = `relatorio-processos-${safeClientName || 'cliente'}-${new Date().toISOString().slice(0, 10)}.xlsx`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(blobUrl)
    notify.showSuccess('Relatório Excel exportado com sucesso!')
  }

  const handleProcessSearch = (event) => {
    event.preventDefault()
    loadProcessPage(appliedFilters, 1, processSearch)
  }

  const filtersChanged = JSON.stringify(filters) !== JSON.stringify(appliedFilters)
  const selectedClient = clientes.find((client) => String(client.id) === String((appliedFilters || filters).clienteId))
  const areas = dashboard?.opcoes?.areas || []

  return (
    <div className="process-reports-page">
      <section className="report-filter-panel">
        <div className="report-filter-heading">
          <div>
            <span className="report-eyebrow">Base analítica de processos</span>
            <h2>{selectedClient ? `Visão analítica — ${selectedClient.nome}` : 'Comece selecionando um cliente'}</h2>
            <p>Indicadores operacionais e financeiros no padrão do relatório ClickBus, usando o último snapshot disponível.</p>
          </div>
          {dashboard ? <div className="report-cache-pill"><i className="fas fa-database" /><span>Atualizado em<strong>{formatDateTime(dashboard.atualizadoEm)}</strong></span></div> : null}
        </div>
        <form onSubmit={applyFilters}>
          <div className="report-filter-start">
            <div className="report-filters-grid report-primary-filters">
              <ClientAutocomplete clients={clientes} disabled={loadingClients} value={filters.clienteId} onChange={(value) => updateFilter('clienteId', value)} />
              <label className="report-area-filter">
                <span>Área do escritório</span>
                <select value={filters.area} onChange={(event) => updateFilter('area', event.target.value)} disabled={!dashboard || !filters.clienteId || String(filters.clienteId) !== String(appliedFilters?.clienteId || '')}>
                  <option value="">Todas as áreas</option>
                  {areas.map((area) => <option key={area} value={area}>{area}</option>)}
                </select>
                <small>Faça a primeira consulta para carregar as áreas disponíveis.</small>
              </label>
            </div>
            <aside className="report-filter-context" aria-live="polite">
              <i className={`fas ${selectedClient ? 'fa-circle-check' : 'fa-chart-line'}`} aria-hidden="true" />
              <div>
                <span>{selectedClient ? 'Cliente selecionado' : 'Pronto para analisar'}</span>
                <strong>{selectedClient?.nome || 'Escolha um cliente para liberar os indicadores'}</strong>
                <small>{selectedClient ? describePeriods(filters) : 'Use os períodos abaixo apenas se precisar refinar o recorte.'}</small>
              </div>
            </aside>
          </div>
          <details className="report-periods"><summary><i className="far fa-calendar" /> Períodos de entrada, distribuição e encerramento</summary><div className="report-period-grid">{[['entrada', 'Entrada'], ['distribuicao', 'Distribuição'], ['encerramento', 'Encerramento']].map(([key, label]) => <fieldset key={key}><legend>{label}</legend><label><span>De</span><input type="date" value={filters[`${key}Inicio`]} onChange={(event) => updateFilter(`${key}Inicio`, event.target.value)} /></label><label><span>Até</span><input type="date" value={filters[`${key}Fim`]} onChange={(event) => updateFilter(`${key}Fim`, event.target.value)} /></label></fieldset>)}</div></details>
          <div className="report-filter-footer">
            <div className="report-export-options">
              <label>
                <input type="checkbox" checked={includeInternalNotes} onChange={(event) => setIncludeInternalNotes(event.target.checked)} />
                <span><strong>Incluir observações internas</strong><small>Desmarcado gera uma versão mais segura para envio ao cliente.</small></span>
              </label>
            </div>
            <div className="report-filter-actions">
              <button type="button" className="btn-secondary" onClick={clearFilters} disabled={!filters.clienteId}>Limpar filtros</button>
              <button type="button" className="btn-secondary report-export-button" onClick={exportExcel} disabled={!dashboard || filtersChanged || exporting} title={filtersChanged ? 'Aplique os filtros antes de exportar.' : 'Exportar o recorte exibido em Excel'}>
                <i className={`fas ${exporting ? 'fa-spinner fa-spin' : 'fa-file-excel'}`} /> {exporting ? 'Gerando Excel...' : 'Exportar relatório'}
              </button>
              <button type="submit" className="btn-primary" disabled={!filters.clienteId || loading}><i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-filter'}`} /> {loading ? 'Calculando...' : 'Aplicar filtros'}</button>
            </div>
          </div>
        </form>
      </section>

      {error ? <div className="report-error"><i className="fas fa-circle-exclamation" />{error}</div> : null}
      {!dashboard && !loading ? <div className="report-empty"><i className="fas fa-chart-pie" /><h3>Seu dashboard analítico aparecerá aqui</h3><p>Escolha um cliente acima e aplique os filtros para visualizar os indicadores.</p></div> : null}
      {loading && !dashboard ? <div className="report-empty"><i className="fas fa-spinner fa-spin" /><h3>Preparando indicadores...</h3></div> : null}

      {dashboard ? <>
        <div className="report-stats-grid report-main-stats-grid">
          <Stat label="Total de processos" value={formatNumber(dashboard.total)} detail={`${formatNumber(dashboard.registrosNoCache)} registros no snapshot`} icon="fa-scale-balanced" />
          <Stat label="Em andamento" value={formatNumber(dashboard.emAndamento)} detail={dashboard.total ? `${Math.round(dashboard.emAndamento * 100 / dashboard.total)}% do recorte` : '0% do recorte'} icon="fa-hourglass-half" tone="blue" />
          <Stat label="Encerrados" value={formatNumber(dashboard.encerrados)} detail={dashboard.total ? `${Math.round(dashboard.encerrados * 100 / dashboard.total)}% do recorte` : '0% do recorte'} icon="fa-circle-check" tone="green" />
          <Stat label="Arquivados" value={formatNumber(dashboard.arquivados)} detail="Status, situação ou fase contendo arquivado" icon="fa-box-archive" tone="purple" />
          <Stat label="Tempo médio" value={dashboard.tempoMedioEncerramentoDias == null ? '—' : `${dashboard.tempoMedioEncerramentoDias} dias`} detail="Da entrada ao encerramento" icon="fa-stopwatch" />
        </div>
        <div className="report-stats-grid report-provision-stats-grid">
          <Stat label="Provisão possível" value={formatCurrency(dashboard.valorProvisaoPossivel)} detail="Soma do recorte" icon="fa-coins" />
          <Stat label="Provisão remota" value={formatCurrency(dashboard.valorProvisaoRemota)} detail="Soma do recorte" icon="fa-shield-halved" tone="blue" />
          <Stat label="Provisão provável" value={formatCurrency(dashboard.valorProvisaoProvavel)} detail="Soma do recorte" icon="fa-triangle-exclamation" tone="purple" />
        </div>
        <div className="report-charts-grid report-expanded-charts-grid">
          <section className="report-card report-chart wide"><div className="report-card-title"><div><span>Evolução temporal</span><h3>Tendência mensal</h3></div></div><div className="report-chart-canvas"><ChartComponent type="line" data={trendData} /></div></section>
          <section className="report-card report-chart"><div className="report-card-title"><div><span>Perspectiva de perda</span><h3>Distribuição de risco</h3></div></div><div className="report-chart-canvas"><ChartComponent type="doughnut" data={lossData} /></div></section>
          <section className="report-card report-chart"><div className="report-card-title"><div><span>Valores de provisão</span><h3>Possível, remota e provável</h3></div></div><div className="report-chart-canvas"><ChartComponent type="bar" data={provisionData} options={currencyChartOptions} /></div></section>
          <section className="report-card report-chart report-area-chart"><div className="report-card-title"><div><span>Distribuição jurídica</span><h3>Processos por área</h3></div></div><div className="report-chart-canvas report-area-chart-canvas"><ChartComponent type="bar" data={areaData} options={horizontalChartOptions} /></div></section>
          <section className="report-card report-chart report-area-chart"><div className="report-card-title"><div><span>Objetos processuais</span><h3>Quantidade por objeto</h3></div></div><div className="report-chart-canvas report-area-chart-canvas"><ChartComponent type="bar" data={objectData} options={horizontalChartOptions} /></div></section>
          <section className="report-card report-chart"><div className="report-card-title"><div><span>Distribuição geográfica</span><h3>Processos por estado</h3></div></div><div className="report-chart-canvas"><ChartComponent type="doughnut" data={stateData} /></div></section>
        </div>
        <AreaSummary items={dashboard.resumoPorArea} />
        <div className="report-rankings-grid"><Ranking title="Por ação" items={dashboard.porAcao} /><Ranking title="Por fase" items={dashboard.porFase} /><Ranking title="Por contrato" items={dashboard.porContrato} /></div>
        <ProcessList page={processPage} loading={loadingProcesses} search={processSearch} onSearchChange={setProcessSearch} onSearch={handleProcessSearch} onPageChange={(page) => loadProcessPage(appliedFilters, page, processSearch)} />
      </> : null}
    </div>
  )
}

export default function ProcessReportsPage() {
  return <GeneralProcessReport />
}
